import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BoxModelConstraint, CellPanel, ColumnComposition, ColumnPanel, Panel, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { CellData, TabData } from '@ddt-web/data-service';
import { DcrUpdateUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { PerformanceColumnComposition, TableUtils } from '../../helpers';

export class PerformanceTableUtil {
	public static PERFORMANCE_TYPE_ID: string = 'performanceType';

	/**
	 * Builds the partner performance table in pdf with gross and net custom data points 
	 * It also adds the performance type column which should be provided in the extractor config. The fee amount will be displayed along with the performance type.
	 * It is intended that each probably tab should have the same columns
	 * Columns headings are based on the gross tab 
	 * 
	 * Will only render the table if all expected tabs are present
	 * Will not render net custom if net is missing
	 * 
	 * @param tabData 
	 * @param dcrKeyPrefix 
	 * @param shouldIncludeAnnualizedDisclaimer 
	 * @param customAdvisoryFee 
	 * @param contentService 
	 */
	public static buildCustomFeePerformanceTable(
		tabData: Array<TabData>,
		dcrKeyPrefix: string,
		shouldIncludeAnnualizedDisclaimer: boolean,
		customAdvisoryFee: number,
		contentService: ContentService,
		customColumnProportions?: number[],
		customGrossBoxModelConstraint?: Partial<BoxModelConstraint>,
		customNetBoxModelConstraint?: Partial<BoxModelConstraint>
	): Array<ColumnPanel> {
		const [grossTabData, netCustomTabData] = tabData;

		const panels: AbstractRenderer<SVGGElement>[] = [];

		// Column proportions
		const rawHeaders = grossTabData.tableData[0].headersX;
		const filteredHeaders = rawHeaders.filter((header: CellData) => header.config.visible);
		const columnProportions = customColumnProportions || [22, 20, ...TableUtils.calculateColumnProportions(filteredHeaders.length - 1, 100 - 22 - 20)];

		// Table definitions
		const grossTableDefinitions = grossTabData ?
			new PerformanceColumnComposition().getPerformanceColumnComposition(grossTabData, columnProportions, customGrossBoxModelConstraint) : null;
		const netCustomTableDefinitions = netCustomTabData ?
			new PerformanceColumnComposition().getPerformanceColumnComposition(netCustomTabData, columnProportions, customNetBoxModelConstraint) : null;

		const headerRow: RowPanel = new RowPanel(
			'Hypo Table Header Row',
			grossTableDefinitions.headers.map((config: ColumnComposition) => config.renderer.render()),
			{ marginBottom: 5, paddingTop: 5, borderBottom: 0.25, borderBottomColor: '#000' },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);

		const rows: Array<RowPanel> = [];
		grossTableDefinitions.body.forEach((grossColumnConfigs: Array<ColumnComposition>, portfolioIndex: number) => {
			const filteredCols = grossColumnConfigs
				.filter((grossColumn: ColumnComposition, cellIndex: number) => {
					if (cellIndex === 0) { // portfolio name
						return true;
					}

					if (grossColumn.cells[0].config.visible) {
						return true;
					}

					return false;
				});

			const rowRenderer = filteredCols.map((config: ColumnComposition, cellIndex: number) => {
				if (cellIndex === 0) { // portfolio name
					return config.renderer.render();
				}

				const panels = [config.renderer.render()]; // gross component

				let netCustomConfig: ColumnComposition;
				if (netCustomTableDefinitions) {
					netCustomConfig = netCustomTableDefinitions.body[portfolioIndex][cellIndex];
				}

				let netCustomComponent: AbstractRenderer<SVGGElement>;
				const isPerfTypeColumn = get(config, 'cells[0].config.id') === PerformanceTableUtil.PERFORMANCE_TYPE_ID;
				if (isPerfTypeColumn) {
					if (netCustomConfig) {
						netCustomComponent = this.getPerformanceTypeLabel(netCustomConfig, customAdvisoryFee, 'PDF_NET_CUSTOM_LABEL', contentService);
					}
				} else {
					if (netCustomConfig) {
						netCustomComponent = netCustomConfig.renderer.render();
					}
				}

				// if there is net/custom column above then we push it to the panels which is instantiated with the gross column 

				if (netCustomComponent && customAdvisoryFee) {
					panels.push(netCustomComponent);
				}

				const columnPanel = new ColumnPanel(
					'',
					panels.map((panel: Panel, rowIndex: number) => new RowPanel('', panel.childArtifacts, this.getRowBoxModelClasses(panels.length, rowIndex)))
				);
				return new CellPanel([columnPanel], { proportion: config.widthProportion }, config.horizontalAlignment);
			});

			rows.push(
				new RowPanel('bodyRow', rowRenderer, this.getRowPanelBoxModelConstraints(portfolioIndex, grossTableDefinitions.body.length), Alignment.Horizontal.Left, Alignment.Vertical.Top)
			);
		});
		const bodyRow: RowPanel = new RowPanel('Performance Table Body Row', [new CellPanel(rows, { proportion: 100 }, Alignment.Horizontal.Left)]);

		panels.push(headerRow, bodyRow);

		if (shouldIncludeAnnualizedDisclaimer) {
			panels.push(new StaticParagraphComponent(dcrKeyPrefix + 'TABLE_P2', '* Annualized', {}, { paddingBottom: 5 }, ['text-condensed']));
		}

		return [new ColumnPanel('Portfolio Performance Section', panels, { marginBottom: 10 }, Alignment.Horizontal.Left)];
	}

	/**
	 * Prepares the performance type table cell including the advisory fee value if net or net custom
	 * note: dcrKey will replace the entire cell content including the advisory fee
	 * 
	 * @param config 
	 * @param fee 
	 * @param dcrKey 
	 * @param contentService 
	 */
	private static getPerformanceTypeLabel(config: ColumnComposition, fee: number, dcrKey: string, contentService: ContentService): CellPanel {
		const combinedLabelComponent = [
			new StaticParagraphComponent(
				dcrKey,
				`${config.cells[0].formattedValue} ${DcrUpdateUtil.getAdvisoryFeeTextForPdf(contentService, fee)}`,
				{},
				{ paddingLeft: 5, paddingRight: 5 }
			)
		];
		return new CellPanel(combinedLabelComponent, { proportion: config.widthProportion }, Alignment.Horizontal.Left);
	}

	private static getRowBoxModelClasses(panelCount: number, rowIndex: number): Partial<BoxModelConstraint> {
		const boxModelConstraints = {};
		switch (panelCount) {
			case 1:
			case 2:
				if (rowIndex > 0) {
					boxModelConstraints['paddingTop'] = 5;
					boxModelConstraints['paddingBottom'] = 5;
				}
				break;
			case 3:
				if (rowIndex === 1) {
					boxModelConstraints['paddingTop'] = 5;
				} else if (rowIndex === 2) {
					boxModelConstraints['paddingTop'] = 5;
					boxModelConstraints['paddingBottom'] = 5;
				}
				break;
		}
		return boxModelConstraints;
	}

	private static getRowPanelBoxModelConstraints(portfolioIndex: number, tableBodyLength: number): Partial<BoxModelConstraint> {
		const boxModelWithSeparator = {
			marginTop: 9,
			paddingBottom: 9,
			borderBottom: 0.25,
			borderBottomColor: '#616161'
		};
		const boxModelFirstRow = {
			marginTop: 9,
			paddingBottom: 9,
			borderBottom: 0.25,
			borderBottomColor: '#616161'
		};
		const boxModelWithoutSeparator = {
			marginTop: 9,
			marginBottom: 9
		};

		const isLastRow = portfolioIndex === tableBodyLength - 1;
		if (isLastRow) {
			return boxModelWithoutSeparator;
		} else if (portfolioIndex === 0) {
			return boxModelFirstRow;
		} else {
			return boxModelWithSeparator;
		}
	}

	public static getPerformanceChartGroupedLegendColumnConfigurationPdf(contentSvc: ContentService, applicationState: any): Array<any> {
		return [
			{
				heading: contentSvc.get('GROUPED_LEGEND_CATEGORY_1_HEADING_PDF', contentSvc.get('GROUPED_LEGEND_CATEGORY_1_HEADING', 'Gross Performance')),
				subHeading: contentSvc.get('GROUPED_LEGEND_CATEGORY_1_SUBHEADING_PDF', contentSvc.get('GROUPED_LEGEND_CATEGORY_1_SUBHEADING', null))
			},
			{
				heading: contentSvc.get('GROUPED_LEGEND_CATEGORY_3_HEADING_PDF', contentSvc.get('GROUPED_LEGEND_CATEGORY_3_HEADING', 'Net'
					+ DcrUpdateUtil.getAdvisoryFeeTextForPdf(contentSvc, applicationState?.performanceState?.customAdvisoryFee))),
				subHeading: contentSvc.get('GROUPED_LEGEND_CATEGORY_3_SUBHEADING_PDF', contentSvc.get('GROUPED_LEGEND_CATEGORY_3_SUBHEADING', null))
			}
		];
	}
}
